import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Receipt, ClipboardList, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getBillingInvoicesByPatient,
  getBillingServices,
  createBillingInvoice,
  updateBillingInvoice,
  recordInvoicePayment,
  getPatientDeposits,
  applyAdvanceToInvoice,
} from '@/lib/api';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import InvoiceEditModal from '@/components/Patient/InvoiceEditModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { InvoiceCard } from '@/components/Billing/InvoiceCard';
import { PaymentRecordModal, InvoiceViewModal } from '@/components/Billing/BillingModals';
import { SettleBalanceModal } from '@/components/Billing/SettleBalanceModal';
import { RecordDepositModal } from '@/components/Billing/RecordDepositModal';
import { BillingSummaryCards, BillingListContainer } from '@/components/Billing/BillingUI';
import type { Invoice, BillingService } from '@/types';

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';

const PAYMENT_MODE_MAP: Record<string, PaymentMode> = {
  cash: 'cash', upi: 'upi', card: 'card',
  cheque: 'cheque', netbanking: 'netbanking',
  'bank transfer': 'netbanking',
  insurance: 'other', other: 'other',
};

function toPaymentMode(method: string | undefined): PaymentMode {
  return PAYMENT_MODE_MAP[(method || '').toLowerCase()] ?? 'cash';
}

interface BillingTabProps {
  patientId: string;
  patient: {
    id: string;
    name: string;
    phone?: string;
    address?: string;
  };
  doctorId: string;
  clinicId: string;
}

export default function BillingTab({ patientId, patient, doctorId, clinicId }: BillingTabProps) {
  const [loading, setLoading]                   = useState(false);
  const [invoices, setInvoices]                 = useState<Invoice[]>([]);
  const [services, setServices]                 = useState<BillingService[]>([]);
  const [searchTerm, setSearchTerm]             = useState('');
  const [statusFilter, setStatusFilter]         = useState<string>('all');
  const [dateRange, setDateRange]               = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const [amountRange, setAmountRange]           = useState({ min: '', max: '' });
  const [currentPage, setCurrentPage]           = useState(1);
  const [itemsPerPage, setItemsPerPage]         = useState(10);
  const [showCreateModal, setShowCreateModal]   = useState(false);
  const [showViewModal, setShowViewModal]       = useState(false);
  const [showEditModal, setShowEditModal]       = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal]     = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showSettleModal, setShowSettleModal]   = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [availableAdvance, setAvailableAdvance] = useState(0);
  const [outstanding, setOutstanding]           = useState(0);
  const [pendingAmount, setPendingAmount]       = useState(0);
  const [selectedInvoice, setSelectedInvoice]   = useState<Invoice | null>(null);

  const { formatCurrency } = useBillingUtils();

  useEffect(() => {
    Promise.all([loadInvoices(), loadDeposits(), loadServices()]);
  }, [patientId, clinicId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange, amountRange, invoices.length]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    toast({
      title: type === 'success' ? 'Success' : 'Error',
      description: message,
      variant: type === 'success' ? 'default' : 'destructive',
    });
  };

  // Status filter — direct comparison, no mapping needed
  const filteredInvoices = useMemo(() => invoices.filter(invoice => {
    const matchesSearch = !searchTerm ||
        invoice.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
        invoice.payment_status === statusFilter;

    let matchesDate = true;
    if (dateRange.from || dateRange.to) {
      const invoiceDate     = new Date(invoice.invoice_date);
      const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());

      if (dateRange.from && dateRange.to) {
        const from = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
        const to   = new Date(dateRange.to.getFullYear(),   dateRange.to.getMonth(),   dateRange.to.getDate());
        matchesDate = invoiceDateOnly >= from && invoiceDateOnly <= to;
      } else if (dateRange.from) {
        const from = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
        matchesDate = invoiceDateOnly >= from;
      } else if (dateRange.to) {
        const to = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
        matchesDate = invoiceDateOnly <= to;
      }
    }

    const matchesAmountRange =
        (!amountRange.min || invoice.total_amount >= parseFloat(amountRange.min)) &&
        (!amountRange.max || invoice.total_amount <= parseFloat(amountRange.max));

    return matchesSearch && matchesStatus && matchesDate && matchesAmountRange;
  }), [invoices, searchTerm, statusFilter, dateRange, amountRange]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getBillingInvoicesByPatient(patientId);
      setInvoices(data || []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      showNotification('error', 'Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const loadDeposits = async () => {
    try {
      const data = await getPatientDeposits(patientId, clinicId);
      setAvailableAdvance(Number(data?.available_advance || 0));
      setOutstanding(Number(data?.outstanding || 0));
      setPendingAmount(Number((data as any)?.pending_amount || 0));
    } catch (error) {
      console.error('Failed to load deposits:', error);
    }
  };

  const loadServices = async () => {
    try {
      const data = await getBillingServices(clinicId, doctorId);
      setServices((data as any) || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      setLoading(true);

      await createBillingInvoice({
        patient_id:      patientId,
        doctor_id:       doctorId,
        clinic_id:       clinicId,
        invoice_date:    invoiceData.invoiceDate || undefined,
        items:           invoiceData.items.map((item: any) => ({
          service_id:      item.service_id      || undefined,
          service_name:    item.service_name,
          category:        item.category        || undefined,
          item_type:       item.item_type        || 'other',
          quantity:        item.quantity,
          unit_price:      item.unit_price,
          discount_amount: item.discount_amount  || 0,
          tax_rate:        item.tax_rate         || 0,
          tax_amount:      item.tax_amount       || 0,
          total_price:     item.total_price,
          notes:           item.notes            || undefined,
        })),
        discount_amount:              invoiceData.discount    || 0,
        discount_type:                invoiceData.discount > 0 ? invoiceData.discountType : undefined,
        tax_amount:                   invoiceData.taxAmount   || 0,
        notes:                        invoiceData.notes       || undefined,
        payments:                     invoiceData.payments    || [],
        balance_forward:              invoiceData.balance_forward              || 0,
        balance_forward_settle:       invoiceData.balance_forward_settle       || 0,
        balance_forward_payment_mode: invoiceData.balance_forward_payment_mode || undefined,
        deposit_amount:               invoiceData.depositAmount      || undefined,
        deposit_payment_mode:         invoiceData.depositPaymentMode || undefined,
      });

      toast({
        title: 'Success',
        description: 'Invoice created successfully',
        action: (
            <ToastAction altText="Book follow-up" onClick={() => setShowFollowUpModal(true)}>
              Book follow-up
            </ToastAction>
        ),
      });
      setShowCreateModal(false);
      await Promise.all([loadInvoices(), loadDeposits()]);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({ title: 'Error', description: 'Failed to create invoice', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => {
    try {
      if (!selectedInvoice || amount <= 0) {
        showNotification('error', 'Please enter a valid payment amount');
        return;
      }
      if (amount > selectedInvoice.balance_due) {
        showNotification('error', 'Payment amount cannot exceed balance due');
        return;
      }
      setLoading(true);
      await recordInvoicePayment(selectedInvoice.id, {
        amount,
        payment_mode:     toPaymentMode(method),
        payment_date:     new Date().toISOString(),
        reference_number: meta?.reference_number || undefined,
        upi_app:          meta?.upi_app          || undefined,
        bank_name:        meta?.bank_name         || undefined,
      });
      showNotification('success', 'Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedInvoice(null);
      await loadInvoices();
    } catch (error: any) {
      showNotification('error', error.response?.data?.error || error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const handleUpdateInvoice = async (invoiceData: any) => {
    try {
      if (!selectedInvoice) return;
      setLoading(true);
      await updateBillingInvoice(selectedInvoice.id, invoiceData);
      toast({ title: 'Success', description: 'Invoice updated successfully' });
      setShowEditModal(false);
      setSelectedInvoice(null);
      await loadInvoices();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to update invoice', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAction = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleApplyAdvance = async (invoice: Invoice) => {
    const applyAmount = Math.min(availableAdvance, Number(invoice.balance_due));
    if (applyAmount <= 0) return;
    const confirmed = window.confirm(
      `Apply ₹${applyAmount.toLocaleString('en-IN')} advance to invoice ${invoice.invoice_number}?\n\nThis will reduce the advance balance by ₹${applyAmount.toLocaleString('en-IN')} and mark the invoice as paid.`
    );
    if (!confirmed) return;
    try {
      setLoading(true);
      await applyAdvanceToInvoice(patientId, clinicId, invoice.id, applyAmount);
      showNotification('success', `₹${applyAmount.toLocaleString('en-IN')} advance applied to ${invoice.invoice_number}`);
      await Promise.all([loadInvoices(), loadDeposits()]);
    } catch (error: any) {
      showNotification('error', error.message || 'Failed to apply advance');
    } finally {
      setLoading(false);
    }
  };

  // ── Stats ────────────────────────────────────────────────────────────────────
  // "Total Billed" — all non-cancelled invoices (not "Revenue" which is a clinic concept)
  const totalBilled = useMemo(() =>
    invoices
      .filter(inv => inv.status !== 'cancelled')
      .reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0),
  [invoices]);

  // Loading skeleton
  if (loading && invoices.length === 0) {
    return (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1, 2].map(i => <Skeleton key={i} className="h-24 rounded-lg" />)}
          </div>
          <Skeleton className="h-16 rounded-lg" />
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
          </div>
        </div>
    );
  }

  const handlePrintInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowPrintModal(true);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
      <div className="space-y-3">

        {/* Account strip — one ledger-style block instead of three floating stat
            cards. Grouping Total/Outstanding/Advance side by side under shared
            dividers reads as "this patient's account", and keeps each balance's
            action (Settle / Add) right where the number that motivates it is. */}
        <Card className="overflow-hidden">
          <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-border">
            <div className="flex-1 flex items-center gap-3 px-4 py-3.5 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-semibold text-foreground truncate">{formatCurrency(totalBilled)}</div>
                {/* "Total Billed" not "Total Revenue" — revenue is a clinic concept */}
                <div className="text-xs text-muted-foreground">Total billed</div>
              </div>
            </div>

            <div className={cn("flex-1 flex items-center gap-3 px-4 py-3.5 min-w-0", outstanding > 0 && "bg-warning/5")}>
              <div className={cn(
                  "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0",
                  outstanding > 0 ? "bg-warning/15" : "bg-primary/10"
              )}>
                <ClipboardList className={cn("w-4 h-4", outstanding > 0 ? "text-warning" : "text-primary")} />
              </div>
              <div className="min-w-0 flex-1">
                <div className={cn("text-base font-semibold truncate", outstanding > 0 ? "text-warning" : "text-foreground")}>
                  {formatCurrency(outstanding)}
                </div>
                <div className="text-xs text-muted-foreground">Outstanding</div>
              </div>
              {outstanding > 0 && (
                  <Button size="sm" variant="outline"
                          className="text-warning border-warning/30 hover:bg-warning/10 flex-shrink-0"
                          onClick={() => setShowSettleModal(true)}>
                    Settle
                  </Button>
              )}
            </div>

            <div className="flex-1 flex items-center gap-3 px-4 py-3.5 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-foreground truncate">{formatCurrency(availableAdvance)}</div>
                <div className="text-xs text-muted-foreground">Advance balance</div>
                {pendingAmount > 0 && availableAdvance >= pendingAmount && (
                  <div className="text-[11px] text-primary mt-0.5">
                    {formatCurrency(pendingAmount)} pending — tap <Wallet className="w-3 h-3 inline" /> on bills to apply
                  </div>
                )}
              </div>
              <Button size="sm" variant="ghost"
                      className="text-primary hover:bg-primary/10 flex-shrink-0"
                      onClick={() => setShowDepositModal(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </Card>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground">Billing history</h3>
            <p className="text-xs text-muted-foreground">
              {invoices.length} {invoices.length === 1 ? 'bill' : 'bills'}
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} size="sm" className="px-4">
            <Plus className="w-4 h-4 mr-2" />
            New Bill
          </Button>
        </div>

        {/* Bills list */}
        <BillingListContainer
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            dateFilter="all"
            onDateChange={() => {}}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            amountRange={amountRange}
            onAmountRangeChange={setAmountRange}
            variant="patient"
            invoices={invoices}
            filteredInvoices={filteredInvoices}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onCreateInvoice={() => setShowCreateModal(true)}
        >
          <Card className="medical-card overflow-hidden">
            {/* No divide-y here: Tailwind's divide-{color} sets the generic
                border-color (all sides) on every child but the first, which was
                clobbering each row's status-coloured border-l-accent (only row 1
                kept its true colour — rows 2+ silently fell back to neutral grey).
                Each InvoiceCard already separates itself via its left accent +
                hover background, so a divider line is redundant anyway. */}
            <div className="[&>*+*]:border-t [&>*+*]:border-t-border">
              {filteredInvoices.length === 0 ? (
                  <div className="p-10 text-center">
                    <div className="w-12 h-12 mx-auto rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <Receipt className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">No bills found</h3>
                    <p className="text-muted-foreground mb-4 text-xs">
                      {searchTerm || statusFilter !== 'all' || dateRange.from || dateRange.to || amountRange.min || amountRange.max
                          ? 'No bills match your current filters'
                          : `${patient.name} doesn't have any billing history yet`
                      }
                    </p>
                    <Button onClick={() => setShowCreateModal(true)} size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Bill
                    </Button>
                  </div>
              ) : (
                  filteredInvoices
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((invoice) => (
                          <InvoiceCard
                              key={invoice.id}
                              invoice={invoice}
                              variant="patient"
                              onView={handleViewInvoice}
                              onEdit={handleEditInvoice}
                              onPrint={handlePrintInvoice}
                              onPayment={handlePaymentAction}
                              onApplyAdvance={availableAdvance > 0 ? handleApplyAdvance : undefined}
                          />
                      ))
              )}
            </div>
          </Card>
        </BillingListContainer>

        {/* Modals */}
        {showCreateModal && (
            <MedicalBillingModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                patient={patient}
                services={services}
                onCreateInvoice={handleCreateInvoice}
                loading={loading}
                clinicId={clinicId}
                doctorId={doctorId}
            />
        )}

        <InvoiceViewModal
            isOpen={showViewModal}
            onClose={() => { setShowViewModal(false); setSelectedInvoice(null); }}
            invoice={selectedInvoice}
            onPrint={handlePrintInvoice}
            onPayment={handlePaymentAction}
            onRefunded={loadInvoices}
            variant="patient"
        />

        <PaymentRecordModal
            isOpen={showPaymentModal}
            onClose={() => { setShowPaymentModal(false); setSelectedInvoice(null); }}
            invoice={selectedInvoice}
            onRecordPayment={handleRecordPayment}
            loading={loading}
        />

        <PrintBillPreviewModal
            open={showPrintModal}
            invoice={selectedInvoice}
            onClose={() => { setShowPrintModal(false); setSelectedInvoice(null); }}
        />

        <InvoiceEditModal
            invoice={selectedInvoice ? {
              ...selectedInvoice,
              patient_id:      selectedInvoice.patient_id      || patientId,
              patient_address: selectedInvoice.patient_address || patient.address,
              doctor_id:       selectedInvoice.doctor_id       || doctorId,
              clinic_id:       selectedInvoice.clinic_id       || clinicId,
              subtotal:        selectedInvoice.subtotal        || selectedInvoice.total_amount,
              discount_amount: selectedInvoice.discount_amount || 0,
              tax_amount:      selectedInvoice.tax_amount      || 0,
              due_date:        selectedInvoice.due_date        || undefined,
              notes:           selectedInvoice.notes           || undefined,
              items:           selectedInvoice.items           || []
            } : null}
            isOpen={showEditModal}
            onClose={() => { setShowEditModal(false); setSelectedInvoice(null); }}
            onSave={handleUpdateInvoice}
            services={services}
            loading={loading}
        />

        <SettleBalanceModal
            isOpen={showSettleModal}
            onClose={() => setShowSettleModal(false)}
            patientId={patientId}
            clinicId={clinicId}
            outstandingAmount={outstanding}
            onSettled={loadInvoices}
        />

        <RecordDepositModal
            isOpen={showDepositModal}
            onClose={() => setShowDepositModal(false)}
            patientId={patientId}
            clinicId={clinicId}
            onRecorded={loadDeposits}
        />

        <AddAppointmentModal
            isOpen={showFollowUpModal}
            onClose={() => setShowFollowUpModal(false)}
            patientId={patientId}
            showPatientSelection={false}
            title="Book Follow-up Appointment"
            onAdded={() => {
              setShowFollowUpModal(false);
              toast({ title: 'Appointment booked', description: 'Follow-up scheduled successfully' });
            }}
        />
      </div>
  );
}